import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listMessages } from "@/lib/api";
import { ArrowLeft, Mail, Inbox as InboxIcon, RefreshCw } from "lucide-react";

export default function Inbox() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const data = await listMessages();
      setItems(data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-cream-50 text-ink-900 grain" data-testid="inbox-page">
      <header className="flex items-center justify-between px-8 md:px-14 py-7 relative z-10">
        <button onClick={() => nav("/")} data-testid="back-to-deck"
                className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to deck
        </button>
        <button onClick={load} data-testid="refresh-inbox"
                className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-500 transition-colors">
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <main className="px-8 md:px-14 pb-20 relative z-10 max-w-5xl mx-auto">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-500 mb-5">/ inbox</p>
        <h1 className="serif font-light text-5xl md:text-6xl tracking-tight leading-tight">Notes from partners.</h1>
        <p className="mt-4 text-ink-900/70 text-lg max-w-2xl">Messages submitted from the contact slide of this deck.</p>

        <div className="mt-12">
          {busy && items.length === 0 ? (
            <div className="mono text-sm text-ink-900/55">loading…</div>
          ) : items.length === 0 ? (
            <div className="border border-ink-900/10 rounded-md p-10 text-center bg-white/55" data-testid="empty-inbox">
              <InboxIcon className="h-6 w-6 text-ink-900/45 mx-auto mb-3" />
              <div className="serif text-2xl">No messages yet.</div>
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/50 mt-2">
                Submissions from the deck's contact form will appear here.
              </div>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((m) => (
                <li key={m.id} className="border border-ink-900/10 rounded-md p-6 bg-white/55 hover:bg-white/80 transition-colors" data-testid={`msg-${m.id}`}>
                  <div className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="serif text-xl">{m.name}{m.company ? <span className="text-ink-900/65"> · {m.company}</span> : null}</div>
                      <a href={`mailto:${m.email}`} className="mono text-[11px] uppercase tracking-[0.22em] text-sun-500 hover:underline flex items-center gap-1.5 mt-1">
                        <Mail className="h-3 w-3" /> {m.email}
                      </a>
                    </div>
                    <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">
                      {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                  <p className="mt-4 text-ink-900/80 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
